import { paiseToRupees } from "./payoutMoney";

export type PayoutDataSource = "mock" | "api";

type JsonBody = Record<string, unknown> | unknown[] | string | number | boolean | null;

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: JsonBody;
  idempotencyKey?: string;
  stepUpToken?: string;
  signal?: AbortSignal;
};

type JwtClaims = Record<string, unknown>;

export class PayoutApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "PayoutApiError";
    this.status = status;
    this.code = code;
  }
}

export const getPayoutDataSource = (): PayoutDataSource => {
  if (process.env.NODE_ENV === "production") return "api";
  return process.env.REACT_APP_PAYOUTS_DATA_SOURCE === "mock" ? "mock" : "api";
};

export const shouldFallbackPayoutReads = () =>
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_PAYOUTS_API_FALLBACK_TO_MOCK === "true";

export const getPayoutApiBaseUrl = () => {
  const raw = process.env.REACT_APP_PAYOUTS_API_BASE_URL || "";
  return raw.replace(/\/+$/, "");
};

export const isPayoutApiMode = () => getPayoutDataSource() === "api";

const parseJwtClaims = (token: string): JwtClaims | null => {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
    return JSON.parse(decoded) as JwtClaims;
  } catch {
    return null;
  }
};

const PAYOUTS_ACCESS_TOKEN_KEY = "payoutsAccessToken";
const PAYOUTS_SCHOOL_ID_KEY = "payoutsSchoolId";
const PAYOUTS_MERCHANT_SCHOOL_ID_KEY = "payoutsMerchantSchoolId";

const getMerchantToken = () => {
  const urlToken = new URLSearchParams(window.location.search).get("token");
  if (urlToken) return urlToken;
  return (
    window.localStorage.getItem("merchantToken") ||
    window.localStorage.getItem("token") ||
    ""
  );
};

const getFirstAllowedPayoutSchoolId = () =>
  (
    process.env.REACT_APP_PAYOUT_ALLOWED_SCHOOL_IDS ||
    process.env.REACT_APP_PAYOUT_ALLOWED_SCHOOL_ID ||
    process.env.REACT_APP_PAYOUT_SCHOOL_ALLOWLIST ||
    ""
  )
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)[0] || "";

/** Prefer live merchant school_id (set from getMerchantQuery); env only as non-prod fallback. */
export const setPayoutsMerchantSchoolId = (schoolId?: string | null) => {
  const trimmed = String(schoolId || "").trim();
  if (trimmed) {
    window.localStorage.setItem(PAYOUTS_MERCHANT_SCHOOL_ID_KEY, trimmed);
  }
};

const getConfiguredSchoolId = () =>
  window.localStorage.getItem(PAYOUTS_MERCHANT_SCHOOL_ID_KEY) ||
  window.localStorage.getItem(PAYOUTS_SCHOOL_ID_KEY) ||
  process.env.REACT_APP_PAYOUTS_SCHOOL_ID ||
  process.env.REACT_APP_SCHOOL_ID ||
  getFirstAllowedPayoutSchoolId() ||
  (process.env.NODE_ENV !== "production" ? "sch_local" : "");

/** Production always uses merchant-exchange. Non-prod defaults to dev-exchange unless forced. */
const shouldUseDevAuthExchange = () => {
  if (process.env.NODE_ENV === "production") return false;
  if (process.env.REACT_APP_PAYOUTS_USE_MERCHANT_EXCHANGE === "true") return false;
  return (
    process.env.REACT_APP_PAYOUTS_AUTH_DEV_MODE !== "false" &&
    process.env.REACT_APP_AUTH_DEV_MODE !== "false"
  );
};

let exchangeTokenPromise: Promise<{ token: string; schoolId: string }> | null = null;

const firstString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const getNestedString = (claims: JwtClaims | null, path: string[]): string | undefined => {
  let current: unknown = claims;
  for (const key of path) {
    if (!current || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return firstString(current);
};

/** School grant embedded in JWT only — do not infer from env (that skipped exchange). */
const getSchoolIdFromTokenGrant = (token: string) => {
  const claims = parseJwtClaims(token);
  const direct =
    firstString(claims?.school_id) ||
    firstString(claims?.schoolId) ||
    firstString(claims?.active_school_id) ||
    getNestedString(claims, ["merchant", "schoolId"]) ||
    getNestedString(claims, ["merchant", "school_id"]);

  if (direct) return direct;

  const schoolIds = claims?.school_ids || claims?.schoolIds;
  if (Array.isArray(schoolIds)) return firstString(schoolIds[0]);

  const schools = claims?.schools;
  if (Array.isArray(schools) && schools[0] && typeof schools[0] === "object") {
    return firstString((schools[0] as Record<string, unknown>).id);
  }

  return undefined;
};

const isPayoutsServiceAccessToken = (token: string) => {
  const claims = parseJwtClaims(token);
  if (!claims) return false;
  const exp = typeof claims.exp === "number" ? claims.exp : Number(claims.exp || 0);
  if (exp && exp <= Math.floor(Date.now() / 1000)) return false;
  const scope = firstString(claims.scope);
  const tokenUse = firstString(claims.token_use) || firstString(claims.tokenUse);
  const audience = firstString(claims.aud);
  return (
    scope === "payouts:write" ||
    tokenUse === "access" ||
    audience === "payouts-service"
  );
};

export const clearPayoutsAuthCache = () => {
  window.localStorage.removeItem(PAYOUTS_ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(PAYOUTS_SCHOOL_ID_KEY);
  exchangeTokenPromise = null;
};

const extractAccessToken = (payload: Record<string, unknown>) =>
  firstString(payload.access_token) ||
  firstString(payload.accessToken) ||
  firstString(payload.token) ||
  firstString((payload.data as Record<string, unknown> | undefined)?.access_token) ||
  firstString((payload.data as Record<string, unknown> | undefined)?.accessToken) ||
  firstString((payload.data as Record<string, unknown> | undefined)?.token);

const extractSchoolId = (payload: Record<string, unknown>, fallback: string) =>
  firstString(payload.school_id) ||
  firstString(payload.schoolId) ||
  firstString((payload.data as Record<string, unknown> | undefined)?.school_id) ||
  firstString((payload.data as Record<string, unknown> | undefined)?.schoolId) ||
  fallback;

const runAuthExchange = async (
  path: "/auth/merchant-exchange" | "/auth/dev-exchange",
  merchantToken: string,
  schoolId: string,
): Promise<{ token: string; schoolId: string }> => {
  const body: Record<string, unknown> = {
    merchant_token: merchantToken,
    school_id: schoolId || undefined,
  };
  if (path === "/auth/dev-exchange") {
    // Local only — do not send elevated roles in production path.
    body.roles = ["MAKER", "CHECKER", "ADMIN", "VIEWER"];
  }

  const payload = await request<Record<string, unknown>>(
    path,
    { method: "POST", body },
    { skipAuth: true },
  );
  const exchanged = extractAccessToken(payload);
  if (!exchanged) {
    throw new PayoutApiError(
      "Payouts auth exchange did not return an access token.",
      401,
      "PAYOUTS_EXCHANGE_FAILED",
    );
  }
  const exchangedSchoolId = extractSchoolId(payload, schoolId);
  window.localStorage.setItem(PAYOUTS_ACCESS_TOKEN_KEY, exchanged);
  if (exchangedSchoolId) {
    window.localStorage.setItem(PAYOUTS_SCHOOL_ID_KEY, exchangedSchoolId);
  }
  return { token: exchanged, schoolId: exchangedSchoolId };
};

const ensurePayoutsAccess = async () => {
  const configuredSchoolId = getConfiguredSchoolId();
  const cachedPayoutsToken = window.localStorage.getItem(PAYOUTS_ACCESS_TOKEN_KEY) || "";
  const merchantToken = getMerchantToken();

  if (
    cachedPayoutsToken &&
    isPayoutsServiceAccessToken(cachedPayoutsToken) &&
    getSchoolIdFromTokenGrant(cachedPayoutsToken)
  ) {
    const schoolId =
      getSchoolIdFromTokenGrant(cachedPayoutsToken) ||
      configuredSchoolId ||
      window.localStorage.getItem(PAYOUTS_SCHOOL_ID_KEY) ||
      "";
    if (schoolId) {
      return { token: cachedPayoutsToken, schoolId };
    }
  }

  if (!merchantToken) {
    throw new PayoutApiError("Missing authorization token for payouts API.", 401, "PAYOUTS_AUTH_MISSING");
  }

  if (!exchangeTokenPromise) {
    const path = shouldUseDevAuthExchange()
      ? "/auth/dev-exchange"
      : "/auth/merchant-exchange";
    exchangeTokenPromise = runAuthExchange(path, merchantToken, configuredSchoolId).catch(
      (err) => {
        exchangeTokenPromise = null;
        clearPayoutsAuthCache();
        throw err;
      },
    );
  }

  return exchangeTokenPromise;
};

/**
 * Probe enrollment for nav gating. Returns true only when exchange succeeds.
 * 403 → not enrolled / school disabled. Does not throw for expected denials.
 */
export async function probePayoutsEnrollment(schoolId?: string): Promise<boolean> {
  if (schoolId) setPayoutsMerchantSchoolId(schoolId);
  if (
    process.env.NODE_ENV !== "production" &&
    process.env.REACT_APP_PAYOUTS_ACCESS_BYPASS === "true"
  ) {
    return true;
  }
  if (process.env.NODE_ENV === "production" && getPayoutDataSource() === "mock") {
    return false;
  }
  if (!getPayoutApiBaseUrl()) return false;
  try {
    clearPayoutsAuthCache();
    await ensurePayoutsAccess();
    return true;
  } catch (err) {
    if (err instanceof PayoutApiError && (err.status === 401 || err.status === 403)) {
      return false;
    }
    return false;
  }
}

export async function ensurePayoutsAccessToken(): Promise<string> {
  const access = await ensurePayoutsAccess();
  return access.token;
}


export const createPayoutCorrelationId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `payout-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createPayoutIdempotencyKey = (scope: string, seed?: string) =>
  `${scope}:${seed || createPayoutCorrelationId()}`;

const buildUrl = (path: string, query?: RequestOptions["query"]) => {
  const base = getPayoutApiBaseUrl();
  if (!base) {
    throw new PayoutApiError(
      "Payouts API base URL is not configured. Set REACT_APP_PAYOUTS_API_BASE_URL.",
      0,
      "PAYOUTS_API_BASE_MISSING",
    );
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${base}/v1${normalizedPath}`);
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
};

async function request<T>(
  path: string,
  options: RequestOptions = {},
  internal?: { skipAuth?: boolean },
): Promise<T> {
  const access = internal?.skipAuth ? undefined : await ensurePayoutsAccess();

  const headers: Record<string, string> = {
    "X-Correlation-Id": createPayoutCorrelationId(),
  };

  if (access) {
    headers.Authorization = `Bearer ${access.token}`;
    headers["X-School-Id"] = access.schoolId;
  }

  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
  if (options.stepUpToken) headers["X-Step-Up-Token"] = options.stepUpToken;

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method || "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (!response.ok) {
    let message = `Payouts API request failed with status ${response.status}.`;
    let code: string | undefined;
    try {
      const payload = (await response.json()) as Record<string, unknown>;
      message = firstString(payload.message) || firstString(payload.error) || message;
      code = firstString(payload.code);
    } catch {
      // Avoid logging response bodies because they can contain PII.
    }
    throw new PayoutApiError(message, response.status, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const unwrapList = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const candidates = [record.data, record.items, record.results, record.rows, record.accounts, record.beneficiaries, record.users, record.roles, record.workflows, record.groups, record.rules, record.reports];
    const match = candidates.find(Array.isArray);
    if (Array.isArray(match)) return match as T[];
  }
  return [];
};

const unwrapRecord = (payload: unknown): Record<string, unknown> => {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (record.data && typeof record.data === "object" && !Array.isArray(record.data)) {
      return record.data as Record<string, unknown>;
    }
    return record;
  }
  return {};
};

export const toRupeesFromWire = (value: unknown) =>
  paiseToRupees(typeof value === "number" ? value : Number(value || 0));

export const payoutApi = {
  dashboardSummary: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/dashboard/summary", { signal }).then(unwrapRecord),
  accounts: (signal?: AbortSignal) =>
    request<unknown>("/accounts", { signal }).then(unwrapList<Record<string, unknown>>),
  accountOverview: (accountId: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/accounts/${encodeURIComponent(accountId)}/overview`, { signal }).then(unwrapRecord),
  accountDetails: (accountId: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/accounts/${encodeURIComponent(accountId)}`, { signal }).then(unwrapRecord),
  accountActivity: (accountId: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>(`/accounts/${encodeURIComponent(accountId)}/activity`, { query, signal }).then(unwrapList<Record<string, unknown>>),
  accountStatus: (accountId: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/accounts/${encodeURIComponent(accountId)}/status`, { signal }).then(unwrapRecord),
  accountBalance: (accountId: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/accounts/${encodeURIComponent(accountId)}/balance`, { signal }).then(unwrapRecord),
  validateAccount: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/accounts/validate", { method: "POST", body, idempotencyKey }),
  createAccount: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/accounts", { method: "POST", body, idempotencyKey, stepUpToken }),
  accountStatement: (accountId: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>(`/accounts/${encodeURIComponent(accountId)}/statement`, { query, signal }).then(unwrapList<Record<string, unknown>>),
  accountStatementEnvelope: (accountId: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/accounts/${encodeURIComponent(accountId)}/statement`, { query, signal }),
  transfersSummary: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<Record<string, unknown>>("/transfers/summary", { query, signal }).then(unwrapRecord),
  paymentSearch: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/payment-search", { query, signal }).then(unwrapList<Record<string, unknown>>),
  paymentDetails: (paymentId: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/payments/${encodeURIComponent(paymentId)}`, { signal }).then(unwrapRecord),
  paymentReceipt: (paymentId: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/payments/${encodeURIComponent(paymentId)}/receipt`, { signal }).then(unwrapRecord),
  paymentStatus: (paymentId: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/payments/${encodeURIComponent(paymentId)}/status`, { signal }),
  paymentLookupByUtr: (utr: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/payment-lookup/utr/${encodeURIComponent(utr)}`, { signal }),
  createPayment: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/payments", { method: "POST", body, idempotencyKey, stepUpToken }),
  submitDraftPayment: (paymentId: string, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/payments/${encodeURIComponent(paymentId)}/submit`, { method: "POST", idempotencyKey, stepUpToken }),
  approvePayment: (paymentId: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/payments/${encodeURIComponent(paymentId)}/approve`, { method: "POST", body, idempotencyKey, stepUpToken }),
  rejectPayment: (paymentId: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/payments/${encodeURIComponent(paymentId)}/reject`, { method: "POST", body, idempotencyKey, stepUpToken }),
  cancelPayment: (paymentId: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/payments/${encodeURIComponent(paymentId)}/cancel`, { method: "POST", body, idempotencyKey, stepUpToken }),
  validateBulkPayments: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/payments/bulk/validate", { method: "POST", body, idempotencyKey }),
  createBulkPayments: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/payments/bulk", { method: "POST", body, idempotencyKey, stepUpToken }),
  beneficiariesSummary: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/beneficiaries/summary", { signal }),
  beneficiaries: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/beneficiaries", { query, signal }).then(unwrapList<Record<string, unknown>>),
  beneficiaryDetails: (id: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/beneficiaries/${encodeURIComponent(id)}`, { signal }),
  createBeneficiary: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/beneficiaries", { method: "POST", body, idempotencyKey, stepUpToken }),
  updateBeneficiary: (id: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/beneficiaries/${encodeURIComponent(id)}`, { method: "PATCH", body, idempotencyKey, stepUpToken }),
  deleteBeneficiary: (id: string, idempotencyKey: string, stepUpToken?: string) =>
    request<void>(`/beneficiaries/${encodeURIComponent(id)}`, { method: "DELETE", idempotencyKey, stepUpToken }),
  beneficiaryNameCheck: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/beneficiaries/name-check", { method: "POST", body, idempotencyKey }),
  verifyBeneficiary: (id: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/beneficiaries/${encodeURIComponent(id)}/verify`, { method: "POST", body, idempotencyKey, stepUpToken }),
  validateBulkBeneficiaries: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/beneficiaries/bulk/validate", { method: "POST", body, idempotencyKey }),
  createBulkBeneficiaries: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/beneficiaries/bulk", { method: "POST", body, idempotencyKey, stepUpToken }),
  ifscLookup: (ifsc: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/utils/ifsc/${encodeURIComponent(ifsc)}`, { signal }),
  pendingApprovals: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/approvals/pending", { query, signal }).then(unwrapList<Record<string, unknown>>),
  approvalQueue: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/approvals/queue", { query, signal }).then(unwrapList<Record<string, unknown>>),
  usersSummary: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/users/summary", { signal }),
  users: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/users", { query, signal }).then(unwrapList<Record<string, unknown>>),
  userDetails: (id: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/users/${encodeURIComponent(id)}`, { signal }),
  createUser: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/users", { method: "POST", body, idempotencyKey, stepUpToken }),
  updateUser: (id: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/users/${encodeURIComponent(id)}`, { method: "PATCH", body, idempotencyKey, stepUpToken }),
  deleteUser: (id: string, idempotencyKey: string, stepUpToken?: string) =>
    request<void>(`/users/${encodeURIComponent(id)}`, { method: "DELETE", idempotencyKey, stepUpToken }),
  changeUserPassword: (id: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/users/${encodeURIComponent(id)}/change-password`, { method: "POST", body, idempotencyKey, stepUpToken }),
  rolesSummary: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/roles/summary", { signal }),
  roles: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/roles", { query, signal }).then(unwrapList<Record<string, unknown>>),
  roleDetails: (id: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/roles/${encodeURIComponent(id)}`, { signal }),
  rolesAccessMatrix: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/roles/access-matrix", { signal }),
  createRole: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/roles", { method: "POST", body, idempotencyKey, stepUpToken }),
  updateRole: (id: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/roles/${encodeURIComponent(id)}`, { method: "PATCH", body, idempotencyKey, stepUpToken }),
  deleteRole: (id: string, idempotencyKey: string, stepUpToken?: string) =>
    request<void>(`/roles/${encodeURIComponent(id)}`, { method: "DELETE", idempotencyKey, stepUpToken }),
  permissions: (signal?: AbortSignal) =>
    request<unknown>("/permissions", { signal }).then(unwrapList<Record<string, unknown>>),
  approvalSummary: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/approval-rules/summary", { signal }),
  approvalRulesMatrix: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/approval-rules/matrix", { signal }).then(unwrapRecord),
  updateApprovalRulesMatrix: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/approval-rules/matrix", { method: "PUT", body, idempotencyKey, stepUpToken }),
  evaluateApprovalRule: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/approval-rules/evaluate", { method: "POST", body, idempotencyKey }),
  approvalWorkflows: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/approval-workflows", { query, signal }).then(unwrapList<Record<string, unknown>>),
  approvalWorkflowDetails: (id: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/approval-workflows/${encodeURIComponent(id)}`, { signal }),
  createApprovalWorkflow: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/approval-workflows", { method: "POST", body, idempotencyKey, stepUpToken }),
  updateApprovalWorkflow: (id: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/approval-workflows/${encodeURIComponent(id)}`, { method: "PATCH", body, idempotencyKey, stepUpToken }),
  deleteApprovalWorkflow: (id: string, idempotencyKey: string, stepUpToken?: string) =>
    request<void>(`/approval-workflows/${encodeURIComponent(id)}`, { method: "DELETE", idempotencyKey, stepUpToken }),
  approvalGroups: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/approval-groups", { query, signal }).then(unwrapList<Record<string, unknown>>),
  approvalGroupDetails: (id: string, signal?: AbortSignal) =>
    request<Record<string, unknown>>(`/approval-groups/${encodeURIComponent(id)}`, { signal }),
  createApprovalGroup: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/approval-groups", { method: "POST", body, idempotencyKey, stepUpToken }),
  updateApprovalGroup: (id: string, body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>(`/approval-groups/${encodeURIComponent(id)}`, { method: "PATCH", body, idempotencyKey, stepUpToken }),
  deleteApprovalGroup: (id: string, idempotencyKey: string, stepUpToken?: string) =>
    request<void>(`/approval-groups/${encodeURIComponent(id)}`, { method: "DELETE", idempotencyKey, stepUpToken }),
  settings: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/settings", { signal }),
  updateSettings: (body: JsonBody, idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/settings", { method: "PATCH", body, idempotencyKey, stepUpToken }),
  capabilities: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/capabilities", { signal }),
  reportsSummary: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<Record<string, unknown>>("/reports/summary", { query, signal }),
  reportsCatalog: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/reports/catalog", { query, signal }).then(unwrapList<Record<string, unknown>>),
  reportsJobs: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<unknown>("/reports/jobs", { query, signal }).then(unwrapList<Record<string, unknown>>),
  createReportJob: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/reports/jobs", { method: "POST", body, idempotencyKey }),
  updateReportJob: (id: string, body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>(`/reports/jobs/${encodeURIComponent(id)}`, { method: "PATCH", body, idempotencyKey }),
  deleteReportJob: (id: string, idempotencyKey: string) =>
    request<void>(`/reports/jobs/${encodeURIComponent(id)}`, { method: "DELETE", idempotencyKey }),
  reportDownload: (id: string, query?: RequestOptions["query"]) =>
    request<Record<string, unknown>>(`/reports/jobs/${encodeURIComponent(id)}/download`, { query }),
  reportsInsights: (query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<Record<string, unknown>>("/reports/insights", { query, signal }),
  stepUpChallenge: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/auth/step-up/challenge", { method: "POST", body, idempotencyKey }),
  stepUpVerify: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/auth/step-up/verify", { method: "POST", body, idempotencyKey }),
  mfaStatus: (signal?: AbortSignal) =>
    request<Record<string, unknown>>("/auth/mfa/status", { signal }),
  mfaTotpEnroll: (idempotencyKey: string) =>
    request<Record<string, unknown>>("/auth/mfa/totp/enroll", { method: "POST", idempotencyKey }),
  mfaTotpConfirm: (body: JsonBody, idempotencyKey: string) =>
    request<Record<string, unknown>>("/auth/mfa/totp/confirm", { method: "POST", body, idempotencyKey }),
  mfaTotpDisable: (idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/auth/mfa/totp/disable", {
      method: "POST",
      idempotencyKey,
      stepUpToken,
    }),
  mfaRegenerateBackupCodes: (idempotencyKey: string, stepUpToken?: string) =>
    request<Record<string, unknown>>("/auth/mfa/backup-codes/regenerate", {
      method: "POST",
      idempotencyKey,
      stepUpToken,
    }),
};

export const isEmptyPayoutPayload = (value: unknown) => {
  if (Array.isArray(value)) return value.length === 0;
  if (!value || typeof value !== "object") return true;
  const record = value as Record<string, unknown>;
  if (Array.isArray(record.data)) return record.data.length === 0;
  if (Array.isArray(record.items)) return record.items.length === 0;
  return Object.keys(record).length === 0;
};
